import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import debounce from 'lodash/debounce'
import { UserSettings } from '@chilipiper/api-type-def'
import { personal as personalService, workspaces as workspaceService } from '@chilipiper/service'
import { compose } from 'lodash/fp'
import { getBookingLinksGrouped } from '../../helpers'
import { ApiBookingLink, BookingLinksGroupBy } from '../../types'
import {
  isOverflowing,
  moveDefaultToFront,
  addDefaultBookingLink,
  movePersonalToFront,
  orderLinksByLatestCreated,
  searchLinks,
  splitFavourites,
  sortLinksByFavouriteOrder,
  groupByWorkspaceName,
} from './helpers'
import { GroupedBookingLinks, UseGroupedBookingLinks } from './types'

export const BOOKING_LINKS_HEIGHT = 280
const PERSONAL_SETTINGS_CACHE_KEY = 'personal-settings'
const USER_WORKSPACES_CACHE_KEY = 'user-workspaces'

export const useScroll = (visibleBookingLinks: ApiBookingLink[]) => {
  const [showScrollButton, setShow] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const onScroll = (evt: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const element = evt.target as HTMLElement
    if (evt.target) {
      const moreToShow = Math.floor(element.scrollHeight - element.scrollTop) > element.clientHeight
      setShow(moreToShow)
    }
  }

  const debouncedOnScroll = useRef(debounce(onScroll, 100))

  const scroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop += BOOKING_LINKS_HEIGHT
    }
  }

  useEffect(() => {
    if (scrollRef.current) {
      setShow(isOverflowing(scrollRef.current))
    }

    return () => {
      debouncedOnScroll.current?.cancel()
    }
  }, [scrollRef, visibleBookingLinks.length])

  return {
    scrollRef,
    showScrollButton,
    scroll: useCallback(scroll, [scrollRef]),
    onScroll: (evt: React.UIEvent<HTMLDivElement, UIEvent>) => {
      evt.persist()
      debouncedOnScroll.current?.(evt)
    },
  }
}

export const usePersonalSettings = (userId?: string) => {
  return useQuery(
    PERSONAL_SETTINGS_CACHE_KEY,
    () => personalService.getUserSettings(userId || ''),
    { enabled: !!userId }
  )
}

export const useWorkspaces = () => {
  return useQuery(USER_WORKSPACES_CACHE_KEY, () => workspaceService.userWorkspacesVisible())
}

export const useSavePersonalSettings = () => {
  const queryClient = useQueryClient()

  return useMutation((settings: UserSettings) => personalService.saveSettings(settings), {
    onMutate: async settings => {
      queryClient.cancelQueries(PERSONAL_SETTINGS_CACHE_KEY)
      const previousSettings = queryClient.getQueryData<UserSettings>(PERSONAL_SETTINGS_CACHE_KEY)

      queryClient.setQueryData(PERSONAL_SETTINGS_CACHE_KEY, settings)

      return { previousSettings }
    },
    onError: (err, settings, onMutateValue?: { previousSettings?: UserSettings }) => {
      queryClient.setQueryData(PERSONAL_SETTINGS_CACHE_KEY, onMutateValue?.previousSettings)
    },
    onSettled: () => {
      queryClient.invalidateQueries(PERSONAL_SETTINGS_CACHE_KEY)
    },
  })
}

export const useGroupedBookingLinks = ({
  search,
  workspaces,
  bookingLinks,
  favouriteTemplates,
  defaultBookingLinkUrl,
}: UseGroupedBookingLinks) =>
  useMemo(() => {
    const visibleBookingLinks: ApiBookingLink[] = compose([
      bookingLinks => addDefaultBookingLink({ bookingLinks, defaultBookingLinkUrl }),
      moveDefaultToFront,
      movePersonalToFront,
      orderLinksByLatestCreated,
      bookingLinks => searchLinks(search, bookingLinks),
    ])(bookingLinks)
    const [favouriteBookingLinks, nonFavouriteBookingLinks] = splitFavourites({
      bookingLinks: visibleBookingLinks,
      favouriteTemplates,
    })
    const sortedFavouriteBookingLinks = sortLinksByFavouriteOrder({
      bookingLinks: favouriteBookingLinks,
      favouriteTemplates,
    })
    const groupedBookingLinks: GroupedBookingLinks = compose([
      groupedBookingLinks => groupByWorkspaceName({ workspaces, groupedBookingLinks }),
      bookingLinks => getBookingLinksGrouped(bookingLinks, BookingLinksGroupBy.Workspace),
    ])(nonFavouriteBookingLinks)

    return {
      favouriteBookingLinks: sortedFavouriteBookingLinks,
      groupedBookingLinks,
      visibleBookingLinks,
    }
  }, [search, workspaces, bookingLinks, favouriteTemplates, defaultBookingLinkUrl])
